import * as React from "react"
import { Calendar, ExternalLink, ImageIcon } from "lucide-react"
import { DialogWidget, DialogWidgetContent, DialogWidgetDescription, DialogWidgetFooter, DialogWidgetHeader, DialogWidgetTitle, DialogWidgetTrigger } from "./dialog-widget"
import { cn } from "../../lib/utils"

export interface ContentEntry {
  // Core fields
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  
  // Event-specific fields
  speaker?: string
  time?: string
  location?: string
  price?: string
  duration?: string
  category?: string
  organizer?: string
  capacity?: string
  status?: 'available' | 'sold-out' | 'cancelled'
  
  // Optional content
  image?: string
  details?: string
  extras?: string
}

interface ModalWidgetProps {
  entry: ContentEntry
  trigger: React.ReactNode
  className?: string
  showImage?: boolean
}

const ModalWidget: React.FC<ModalWidgetProps> = ({ entry, trigger, className, showImage = true, ...props }) => {
  return (
    <DialogWidget {...props}>
      <DialogWidgetTrigger asChild>
        {trigger}
      </DialogWidgetTrigger>
      <DialogWidgetContent className={cn("dsw-max-w-2xl", className)}>
        <DialogWidgetHeader>
          <DialogWidgetTitle className="dsw-flex dsw-items-center dsw-gap-2 dsw-mb-2">
            {entry.title}
            <span className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground dsw-font-normal">
              <Calendar className="dsw-h-3 dsw-w-3" />
              {entry.date}
              {` • ${entry.time || 'Time TBA'}`}
            </span>
          </DialogWidgetTitle>
          <DialogWidgetDescription>
            {entry.description}
          </DialogWidgetDescription>
        </DialogWidgetHeader>

        {/* Event Details Section */}
        <div className="dsw-space-y-3">
          <div className="dsw-flex dsw-items-center dsw-gap-2">
            <strong className="dsw-text-sm">Speaker:</strong>
            <span className="dsw-text-sm">{entry.speaker || 'Speaker TBA'}</span>
          </div>
          {entry.location && (
            <div className="dsw-flex dsw-items-center dsw-gap-2">
              <strong className="dsw-text-sm">Location:</strong>
              <span className="dsw-text-sm">{entry.location}</span>
            </div>
          )}
          {entry.price && (
            <div className="dsw-flex dsw-items-center dsw-gap-2">
              <strong className="dsw-text-sm">Price:</strong>
              <span className="dsw-text-sm dsw-font-medium dsw-text-green-600">{entry.price}</span>
            </div>
          )}
          {entry.duration && (
            <div className="dsw-flex dsw-items-center dsw-gap-2">
              <strong className="dsw-text-sm">Duration:</strong>
              <span className="dsw-text-sm">{entry.duration}</span>
            </div>
          )}
          {entry.organizer && (
            <div className="dsw-flex dsw-items-center dsw-gap-2">
              <strong className="dsw-text-sm">Organizer:</strong>
              <span className="dsw-text-sm">{entry.organizer}</span>
            </div>
          )}
          {entry.capacity && (
            <div className="dsw-flex dsw-items-center dsw-gap-2">
              <strong className="dsw-text-sm">Capacity:</strong>
              <span className="dsw-text-sm">{entry.capacity}</span>
            </div>
          )}
          {entry.details && (
            <div>
              <strong className="dsw-text-sm dsw-block dsw-mb-1">Additional Details:</strong>
              <p className="dsw-text-sm dsw-text-muted-foreground">{entry.details}</p>
            </div>
          )}
          {entry.status && entry.status !== 'available' && (
            <div className="dsw-pt-2">
              <span className={cn(
                "dsw-inline-block dsw-px-3 dsw-py-1 dsw-rounded dsw-text-sm dsw-font-medium",
                entry.status === 'sold-out' && "dsw-bg-red-100 dsw-text-red-800",
                entry.status === 'cancelled' && "dsw-bg-gray-100 dsw-text-gray-800"
              )}>
                {entry.status === 'sold-out' ? 'Sold Out' : 'Cancelled'}
              </span>
            </div>
          )}
        </div>
        
        {showImage && (
          <div className="dsw-aspect-video dsw-bg-muted dsw-flex dsw-items-center dsw-justify-center dsw-rounded-lg dsw-my-4">
            {entry.image ? (
              <img 
                src={entry.image} 
                alt={entry.title}
                className="dsw-w-full dsw-h-full dsw-object-cover dsw-rounded-lg"
              />
            ) : (
              <ImageIcon className="dsw-h-16 dsw-w-16 dsw-text-muted-foreground" />
            )}
          </div>
        )}

        <DialogWidgetFooter>
          <a 
            href={entry.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="dsw-inline-flex dsw-items-center dsw-justify-center dsw-whitespace-nowrap dsw-text-sm dsw-font-medium dsw-ring-offset-background dsw-transition-colors dsw-focus-visible:dsw-outline-none dsw-focus-visible:dsw-ring-2 dsw-focus-visible:dsw-ring-ring dsw-focus-visible:dsw-ring-offset-2 dsw-disabled:dsw-pointer-events-none dsw-disabled:dsw-opacity-50 dsw-cursor-pointer dsw-bg-white dsw-text-black dsw-border dsw-border-gray-200 hover:dsw-bg-black hover:dsw-text-white dsw-h-10 dsw-px-4 dsw-py-2 dsw-rounded-lg"
          >
            <ExternalLink className="dsw-h-4 dsw-w-4 dsw-mr-2" />
            {entry.linkText || "Open Link"}
          </a>
        </DialogWidgetFooter>
      </DialogWidgetContent>
    </DialogWidget>
  )
}

export { ModalWidget } 